import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { Loader2 } from "lucide-react";
import { formatDateTime } from "@/utils/dateUtils";
import { whatsappApi } from "@/services/whatsapp-api";

function semAcento(texto: string): string {
  return texto
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();
}

/** Destaca o termo sem olhar acento nem caixa, como a busca do servidor. */
function Destaque({ texto: original, termo }: { texto: string; termo: string }) {
  const texto = original.normalize("NFC");
  const normalizado = semAcento(texto);
  const alvo = semAcento(termo);
  const partes: { trecho: string; achou: boolean }[] = [];
  let inicio = 0;
  let posicao = normalizado.indexOf(alvo);
  while (alvo && posicao !== -1) {
    partes.push({ trecho: texto.slice(inicio, posicao), achou: false });
    partes.push({
      trecho: texto.slice(posicao, posicao + alvo.length),
      achou: true,
    });
    inicio = posicao + alvo.length;
    posicao = normalizado.indexOf(alvo, inicio);
  }
  partes.push({ trecho: texto.slice(inicio), achou: false });
  return (
    <>
      {partes.map((parte, indice) =>
        parte.achou ? (
          <mark key={indice} className="rounded bg-amber-200/70 px-0.5 text-foreground">
            {parte.trecho}
          </mark>
        ) : (
          <span key={indice}>{parte.trecho}</span>
        ),
      )}
    </>
  );
}

/**
 * Resultado da busca nas mensagens já espelhadas, da mais recente para a mais
 * antiga. "Ver no contexto" abre a conversa com a mensagem destacada.
 */
export function WhatsAppBuscaMensagens({
  termo,
  onVerNoContexto,
}: {
  termo: string;
  onVerNoContexto: (conversationId: string, messageId: string) => void;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["whatsapp-busca", termo],
    queryFn: () => whatsappApi.buscarMensagens(termo),
    enabled: termo.length >= 3,
  });

  if (termo.length < 3) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">Digite pelo menos 3 letras.</p>
    );
  }
  if (isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (error) {
    return <p className="py-6 text-center text-sm text-destructive">Não deu para buscar agora.</p>;
  }
  const mensagens = data?.mensagens ?? [];
  if (mensagens.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        Nenhuma mensagem com "{termo}".
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        {data?.limite_atingido
          ? `Mostrando as ${mensagens.length} mais recentes. Refine a busca para ver outras.`
          : `${mensagens.length} ${mensagens.length === 1 ? "mensagem" : "mensagens"}`}
      </p>
      <ul className="divide-y rounded-md border">
        {mensagens.map((mensagem) => (
          <li key={mensagem.message_id} className="space-y-1 px-4 py-3 text-sm">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <span>{formatDateTime(mensagem.created_at)}</span>
              {mensagem.freelancer_id ? (
                <Link
                  to={`/freelancers/${mensagem.freelancer_id}`}
                  className="font-medium text-primary hover:underline"
                >
                  {mensagem.freelancer_nome ?? "Freelancer"}
                </Link>
              ) : (
                <span className="font-medium text-foreground">
                  {mensagem.contact_name ?? mensagem.telefone ?? "Contato"}
                </span>
              )}
              <span>{mensagem.autor}</span>
              <button
                type="button"
                className="ml-auto hover:text-foreground"
                onClick={() => onVerNoContexto(mensagem.conversation_id, mensagem.message_id)}
              >
                Ver no contexto
              </button>
            </div>
            <p className="whitespace-pre-wrap">
              <Destaque texto={mensagem.texto} termo={termo} />
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}
